import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PencilRuler } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Loader } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import { PROJECT_STATUS, PROJECT_TYPES, ProjectThread } from "@/pages/client/ProjectPage";
import Modal, { TextArea, SelectField } from "@/components/Modal";
import { toast } from "sonner";

const FILTERS = [["all", "Tous"], ...Object.entries(PROJECT_STATUS).map(([k, v]) => [k, v.label])];

export default function AdminProjects() {
  const [projects, setProjects] = useState(null);
  const [tab, setTab] = useState("all");
  const [active, setActive] = useState(null);
  const [statusFor, setStatusFor] = useState(null);
  const load = useCallback(() => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])), []);
  useEffect(() => { load(); }, [load]);

  const genQuote = async (p, send = false) => {
    try { const { data } = await api.post(`/admin/projects/${p.id}/create-quote`, null, { params: send ? { send: true } : {} }); toast.success(send ? `Devis ${data.quote_number} envoyé au client` : `Devis ${data.quote_number} créé (statut « Validé »)`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const list = tab === "all" ? (projects || []) : (projects || []).filter((p) => p.status === tab);

  return (
    <div>
      <PageTitle>Projets clients</PageTitle>
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`project-filter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === k ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{label}</button>
        ))}
      </div>
      {!projects ? <Loader /> : (
        <Panel className="divide-y divide-slate-100">
          {list.length === 0 && <p className="p-4 text-sm text-slate-400">Aucun projet.</p>}
          {list.map((p) => (
            <div key={p.id} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`admin-project-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-num text-xs text-slate-400">{p.project_number}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PROJECT_STATUS[p.status]?.color}`}>{PROJECT_STATUS[p.status]?.label}</span>
                </div>
                <p className="font-semibold text-slate-800 line-clamp-1">{p.title}</p>
                <p className="text-xs text-slate-400">{p.customer_name} · {p.customer_phone || "—"} · {formatDate(p.updated_at)}</p>
              </div>
              <div className="ml-auto flex gap-2 items-center">
                {p.linked_quote_id
                  ? <Link to={`/admin/devis?focus=${p.linked_quote_id}`} className="text-xs text-green-600 font-semibold whitespace-nowrap underline hover:text-green-700" data-testid={`project-linked-quote-${p.id}`}>Devis {p.linked_quote_number}</Link>
                  : (
                    <>
                      <button onClick={() => genQuote(p, false)} className="border border-[#C85A32]/40 text-[#C85A32] text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#FDF3EE]" data-testid={`project-genquote-${p.id}`}>Générer devis</button>
                      <button onClick={() => genQuote(p, true)} className="bg-[#C85A32] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#A94520]" data-testid={`project-sendquote-${p.id}`}>Générer &amp; envoyer</button>
                    </>
                  )}
                <button onClick={() => setStatusFor(p)} className="border border-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`project-status-btn-${p.id}`}>Statut</button>
                <button onClick={() => setActive(p.id)} className="bg-[#0F172A] text-white text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`project-open-${p.id}`}>Ouvrir</button>
              </div>
            </div>
          ))}
        </Panel>
      )}
      {active && <ProjectThread id={active} staff onClose={() => setActive(null)} onChanged={load} />}
      {statusFor && <StatusForm project={statusFor} onClose={() => setStatusFor(null)} onDone={load} />}
    </div>
  );
}

function StatusForm({ project, onClose, onDone }) {
  const [status, setStatus] = useState(project.status);
  const [note, setNote] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/admin/projects/${project.id}/status`, { status, note }); toast.success("Statut mis à jour"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`${project.project_number} — Statut`} onClose={onClose} testid="project-status-form">
      <form onSubmit={submit} className="space-y-3">
        <SelectField label="Nouveau statut" value={status} onChange={setStatus} testid="project-status-select" placeholder={null}
          options={Object.entries(PROJECT_STATUS).map(([k, v]) => ({ value: k, label: v.label }))} />
        <TextArea label="Note (envoyée au client)" value={note} onChange={setNote} testid="project-status-note" rows={2} placeholder="Optionnel" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="project-status-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

export { PROJECT_TYPES };
